import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha
} from "@mui/material";
import {Pencil, Trash2, UserPlus, Undo2, AlertCircle} from "lucide-react";
import {
  useListUsersQuery,
  useDeleteUserMutation,
  useRestoreUserMutation,
  UserAdminFieldsFragment
} from "../../graphql/graphql";
import {useQueryClient} from "@tanstack/react-query";
import {useConfirmDialog} from "../../hooks/use-confirm-dialog";
import {useState} from "react";
import {UserEditDialog} from "../../components/pages/Admin/UserEditDialog";
import {UserCreateDialog} from "../../components/pages/Admin/UserCreateDialog";
import {roleColors, ROLES} from "../../components/pages/Admin/constants";

import {LoadingPage} from "../LoadingPage";
import {useUserData} from "../../hooks/use-user-data-provider";
import {Permission} from "../../utils/permissions";
import {NotFoundPage} from "../NotFoundPage";
import toast from "react-hot-toast";
import {Link} from "react-router-dom";

export const UserAdminPage = () => {
  const {data, isLoading} = useListUsersQuery();
  const {mutateAsync: deleteUser} = useDeleteUserMutation();
  const {mutateAsync: restoreUser} = useRestoreUserMutation();
  const queryClient = useQueryClient();
  const queryKey = useListUsersQuery.getKey();
  const {permissions, user: currentUser} = useUserData();
  const [editUser, setEditUser] = useState<{
    id: string;
    name: string;
    email: string;
    roles: string[];
    defaultApprovalFlowId?: string | null;
  } | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const confirmDialog = useConfirmDialog();
  
  
  const handleRestore = async (userId: string, userName: string) => {
    confirmDialog.open({
      fullWidth: true,
      icon: <Undo2 color={"primary"}/>,
      title: (
        <Typography
          sx={{color: "primary.contrastText"}}
          fontWeight={"bold"}
        >
          ユーザーの復元
        </Typography>
      ),
      children: (
        <Stack direction={"column"} spacing={2} minWidth={"250px"}>
          <Typography>{`${userName}を復元してもよろしいですか？`}</Typography>
          <Stack direction={"row"} spacing={2}>
            <Button
              fullWidth
              variant={'outlined'}
              onClick={async () => confirmDialog.close()}
            >
              キャンセル
            </Button>
            <Button
              fullWidth
              variant={'contained'}
              onClick={async () => {
                try {
                  const result = await restoreUser({
                    userId,
                  });
                  
                  if (result?.restoreUser) {
                    // キャッシュを更新
                    queryClient.setQueryData(
                      queryKey,
                      (oldData: any) => ({
                        listUsers: oldData.listUsers.map((user: any) => {
                          if (user.id === userId) {
                            return {...user, isDeleted: false}
                          }
                          return user
                        })
                      })
                    );
                    toast.success("ユーザーを復元しました");
                    confirmDialog.close();
                  }
                } catch (error) {
                  console.error("Failed to restore user:", error);
                  toast.error("ユーザーの復元に失敗しました");
                }
              }}
            >
              復元する
            </Button>
          </Stack>
        </Stack>
      ),
    });
  };
  
  const handleDelete = async (userId: string, userName: string) => {
    confirmDialog.open({
      fullWidth: true,
      icon: <Trash2 color={"error"}/>,
      title: (
        <Typography
          sx={{color: "primary.contrastText"}}
          fontWeight={"bold"}
        >
          ユーザーの削除
        </Typography>
      ),
      children: (
        <Stack direction={"column"} spacing={2} minWidth={"250px"}>
          <Typography>{`${userName}を削除してもよろしいですか？`}</Typography>
          <Stack direction={"row"} spacing={2}>
            <Button
              fullWidth
              variant={'outlined'}
              onClick={async () => confirmDialog.close()}
            >
              キャンセル
            </Button>
            <Button
              fullWidth
              variant={'contained'}
              color="error"
              onClick={async () => {
                try {
                  const result = await deleteUser({
                    userId,
                  });
                  
                  if (result?.deleteUser) {
                    // キャッシュを更新
                    queryClient.setQueryData(
                      queryKey,
                      (oldData: any) => ({
                        listUsers: oldData.listUsers.map((user: any) => {
                          if (user.id === userId) {
                            return {...user, isDeleted: true}
                          }
                          return user
                        })
                      })
                    );
                    toast.success("ユーザーを削除しました");
                    confirmDialog.close();
                  }
                } catch (error) {
                  console.error("Failed to delete user:", error);
                  toast.error("ユーザーの削除に失敗しました");
                }
              }}
            >
              削除する
            </Button>
          </Stack>
        </Stack>
      ),
    });
  };
  
  if (isLoading) {
    return <LoadingPage/>;
  }
  
  // 権限チェック
  if (!permissions.includes(Permission.WriteUsers)) {
    return <NotFoundPage/>;
  }
  return (
    <Box sx={{ p: 4, maxWidth: 1400, margin: '0 auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography 
          variant="h4" 
          sx={{
            fontWeight: 'bold',
          }}
        >
          ユーザー管理
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsCreateDialogOpen(true)}
          startIcon={<UserPlus size={20}/>}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          ユーザーを追加
        </Button>
      </Box>
      
      <TableContainer 
        component={Paper} 
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          overflow: 'hidden',
          '& .MuiTableCell-root': {
            borderColor: 'rgba(224, 224, 224, 0.4)',
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  py: 2,
                  '&:first-of-type': {
                    borderTopLeftRadius: 8,
                  },
                  '&:last-child': {
                    borderTopRightRadius: 8,
                  },
                }}
              >
                名前
              </TableCell>
              <TableCell 
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  py: 2,
                }}
              >
                メールアドレス
              </TableCell>
              <TableCell 
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  py: 2,
                  width: "25%"
                }}
              >
                ロール
              </TableCell>
              <TableCell 
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  py: 2,
                }}
              >
                組織
              </TableCell>
              <TableCell 
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  py: 2,
                }}
              >
                デフォルト承認フロー
              </TableCell>
              <TableCell 
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  py: 2,
                  width: "120px",
                  textAlign: "center"
                }}
              >
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.listUsers.map((user) => (
              <TableRow key={user.id} sx={{
                bgcolor: user.isDeleted ? "rgba(0, 0, 0, 0.05)" : "inherit",
                '&:hover': {
                  bgcolor: user.isDeleted ? "rgba(0, 0, 0, 0.08)" : "rgba(0, 0, 0, 0.03)",
                },
                transition: 'background-color 0.2s ease',
              }} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {user.isDeleted && (
                      <AlertCircle size={16} color="#f44336" />
                    )}
                    <Typography
                      sx={{
                        textDecoration: user.isDeleted ? 'line-through' : 'none',
                        color: user.isDeleted ? 'text.secondary' : 'text.primary'
                      }}
                    >
                      {user.name}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      color: user.isDeleted ? 'text.secondary' : 'text.primary'
                    }}
                  >
                    {user.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {user.roles.map((roleId) => {
                      const role = ROLES.find((r) => r.id === roleId);
                      const colors = roleColors[roleId] || { color: "grey.700", backgroundColor: "grey.100" };
                      return (
                        <Chip
                          key={roleId}
                          label={role?.label || roleId}
                          size="small"
                          sx={{
                            color: colors.color,
                            backgroundColor: colors.backgroundColor,
                            fontWeight: 600,
                            opacity: user.isDeleted ? 0.7 : 1,
                            '&:hover': {
                              backgroundColor: alpha(colors.backgroundColor, 0.8),
                            }
                          }}
                        />
                      );
                    })}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      color: user.isDeleted ? 'text.secondary' : 'text.primary'
                    }}
                  >
                    {user.organization?.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {user.defaultApprovalFlow ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Link 
                        to={`/admin/approvalFlow/${user.defaultApprovalFlow?.id}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <Typography 
                          sx={{ 
                            color: user.isDeleted ? 'text.secondary' : 'primary.main',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: user.isDeleted ? 0.7 : 1,
                            textDecoration: 'none',
                            '&:hover': {
                              color: user.isDeleted ? 'text.secondary' : 'primary.dark',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {user.defaultApprovalFlow.name}
                        </Typography>
                      </Link>
                    </Stack>
                  ) : (
                    <Typography 
                      sx={{ 
                        color: 'text.secondary', 
                        fontStyle: 'italic',
                        fontSize: '0.875rem',
                        opacity: user.isDeleted ? 0.7 : 1
                      }}
                    >
                      未設定
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    {user.id !== currentUser?.id && (
                      user.isDeleted ? (
                        <IconButton
                        onClick={() => handleRestore(user.id, user.name)}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          backgroundColor: alpha('#1976d2', 0.04),
                          border: '1px solid',
                          borderColor: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Undo2 size={18}/>
                      </IconButton>
                    ) : (
                      <>
                          <IconButton
                            onClick={() =>
                              setEditUser({
                                id: user.id,
                                name: user.name,
                                email: user.email,
                                roles: user.roles,
                                defaultApprovalFlowId: user.defaultApprovalFlowId,
                              })
                            }
                            size="small"
                            sx={{
                              color: 'primary.main',
                              backgroundColor: alpha('#1976d2', 0.04),
                              border: '1px solid',
                              borderColor: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <Pencil size={18}/>
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(user.id, user.name)}
                            size="small"
                            sx={{
                              color: 'error.main',
                              backgroundColor: alpha('#d32f2f', 0.04),
                              border: '1px solid',
                              borderColor: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.main',
                                color: 'white',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <Trash2 size={18}/>
                          </IconButton>
                        </>
                      )
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {data?.listUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">
                    ユーザーが存在しません
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {editUser && (
        <UserEditDialog
          user={editUser}
          onClose={() => setEditUser(null)}
          onComplete={(updatedUser: UserAdminFieldsFragment) => {
            setEditUser(null);
            // キャッシュを更新
            queryClient.setQueryData(
              queryKey,
              (oldData: any) => ({
                listUsers: oldData.listUsers.map((user: any) =>
                  user.id === updatedUser.id ? updatedUser : user
                )
              })
            );
            toast.success("ユーザー情報を更新しました");
          }}
        />
      )}
      
      <UserCreateDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onComplete={(newUser: UserAdminFieldsFragment) => {
          setIsCreateDialogOpen(false);
          // キャッシュを更新
          queryClient.setQueryData(
            queryKey,
            (oldData: any) => ({
              listUsers: [...(oldData?.listUsers || []), newUser]
            })
          );
          toast.success("ユーザーを作成しました");
        }}
      />
    </Box>
  );
};
